import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { cancelDeviceBinding, createDeviceBinding, getDeviceBindingStatus, type DeviceBindingCreate, type DeviceBindingStatus } from './deviceBinding';
import { PairingQrError, parsePairingInput, type PairingInput } from './pairingQr';
import { revokedTombstones } from './revokedTombstones';
import {
  buildPairingProvisioningHandoff,
  pairingProvisioningMethods,
  preferredPairingProvisioningMethod,
  type PairingProvisioningHandoff,
  type PairingProvisioningMethod,
} from './pairingProvisioning';
import { browserPosixDefault } from './settings/timezones';

interface PairingPanelProps {
  onClose?: () => void;
  onBound?: () => void;
}

const PAIRING_STATUS_POLL_MS = 2_000;

function pairingErrorKey(e: unknown): string {
  if (e instanceof PairingQrError) return `devices.pairingError_${e.code}`;
  return 'devices.pairingError_INVALID_FIELD';
}

function formatApiError(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (${e.code})`;
  return e instanceof Error ? e.message : String(e);
}

function methodLabel(method: string): string {
  return method === 'ble' ? 'BLE' : 'AP';
}

function handoffMethodLabel(method: PairingProvisioningMethod): string {
  return methodLabel(method);
}

function isTerminalStatus(status: DeviceBindingStatus | null): boolean {
  return status?.status === 'cancelled' || status?.status === 'bound' || status?.status === 'failed';
}

function PairingHandoff({ handoff }: { handoff: PairingProvisioningHandoff }) {
  const { t } = useTranslation();
  const availabilityText =
    handoff.deliveryState === 'direct'
      ? t('devices.pairingProvisioningAvailability_direct')
      : t('devices.pairingProvisioningAvailability_nativeProviderNoFallback', {
        method: handoffMethodLabel(handoff.preferred),
      });
  return (
    <section className="dv-pairing-summary dv-pairing-handoff" aria-live="polite">
      <h3>{t('devices.pairingProvisioningTitle')}</h3>
      <p className="text-secondary dv-pairing-message">
        {t(`devices.pairingProvisioningNext_${handoff.preferred}`)}
      </p>
      <p className="text-secondary dv-pairing-message" data-testid="pairing-provisioning-availability">
        {availabilityText}
      </p>
      <dl className="dv-kv">
        <dt>{t('devices.pairingProvisioningPreferred')}</dt>
        <dd>{handoffMethodLabel(handoff.preferred)}</dd>
        <dt>{t('devices.pairingMethods')}</dt>
        <dd>{handoff.methods.map(handoffMethodLabel).join(' / ')}</dd>
        {handoff.deviceId && (
          <>
            <dt>{t('devices.pairingDeviceId')}</dt>
            <dd>{handoff.deviceId}</dd>
          </>
        )}
        {handoff.softApSsid && (
          <>
            <dt>{t('devices.pairingSoftAp')}</dt>
            <dd>{handoff.softApSsid}</dd>
            <dt>{t('devices.pairingSoftApPassword')}</dt>
            <dd>{handoff.softApPassphrase ? t('devices.pairingSoftApPasswordIncluded') : t('devices.pairingSoftApPasswordMissing')}</dd>
          </>
        )}
      </dl>
      <p className="text-secondary dv-pairing-message" data-testid="pairing-provisioning-candidates">
        {t('devices.pairingProvisioningCandidates', {
          candidates: handoff.candidates.map((candidate) => {
            const state = t(`devices.pairingProvisioningCandidateState_${candidate.deliveryState}`);
            const fallback = candidate.fallback ? t('devices.pairingProvisioningCandidateFallback') : '';
            return `${handoffMethodLabel(candidate.method)}${fallback}: ${state}`;
          }).join(' / '),
        })}
      </p>
    </section>
  );
}

export function PairingPanel({ onClose, onBound }: PairingPanelProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [parsed, setParsed] = useState<PairingInput | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [binding, setBinding] = useState<DeviceBindingCreate | null>(null);
  const [status, setStatus] = useState<DeviceBindingStatus | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'create' | 'status' | 'cancel' | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PairingProvisioningMethod | null>(null);
  const notifiedBoundBinding = useRef<string | null>(null);

  const qr = parsed?.kind === 'qr' ? parsed.payload : null;
  const methods = useMemo(() => (qr ? qr.methods.map(methodLabel).join(' / ') : ''), [qr]);
  const availableMethods = useMemo(() => (parsed ? pairingProvisioningMethods(parsed) : []), [parsed]);
  const activeSelectedMethod = selectedMethod ?? preferredPairingProvisioningMethod(availableMethods);
  const hasActiveBinding = binding !== null && !isTerminalStatus(status);
  const activeBindingId = hasActiveBinding ? binding?.binding_id ?? null : null;
  const handoff = useMemo(
    // common/22 §3: seed the device timezone (POSIX) from the browser/OS zone at
    // the UI edge (kept out of the pure builder so it stays deterministic/testable).
    () => (parsed && binding ? buildPairingProvisioningHandoff(parsed, binding, activeSelectedMethod, {}, browserPosixDefault() || undefined) : null),
    [parsed, binding, activeSelectedMethod]
  );

  const applyStatus = useCallback((next: DeviceBindingStatus, fallbackBindingId: string) => {
    setStatus(next);
    if (next.status !== 'bound') return;
    // 解綁後 30s 內重配同一台:先清撤銷 tombstone,否則 onBound 觸發的 reload 會把它濾掉(APP#123)。
    if (next.device_id) revokedTombstones.clear(next.device_id);
    const completedBindingId = next.binding_id ?? fallbackBindingId;
    if (notifiedBoundBinding.current === completedBindingId) return;
    notifiedBoundBinding.current = completedBindingId;
    onBound?.();
  }, [onBound]);

  useEffect(() => {
    if (!activeBindingId || busy !== null) return;

    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const next = await getDeviceBindingStatus(activeBindingId);
        if (!cancelled) {
          setApiError(null);
          applyStatus(next, activeBindingId);
        }
      } catch (e) {
        if (!cancelled) setApiError(formatApiError(e));
      } finally {
        inFlight = false;
      }
    };

    const id = window.setInterval(() => {
      void poll();
    }, PAIRING_STATUS_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [activeBindingId, applyStatus, busy]);

  const parse = () => {
    if (hasActiveBinding) return;
    setParseError(null);
    setApiError(null);
    setBinding(null);
    setStatus(null);
    notifiedBoundBinding.current = null;
    try {
      const next = parsePairingInput(input);
      setParsed(next);
      const nextMethods = pairingProvisioningMethods(next);
      setSelectedMethod(preferredPairingProvisioningMethod(nextMethods));
    } catch (e) {
      setParsed(null);
      setSelectedMethod(null);
      setParseError(t(pairingErrorKey(e)));
    }
  };

  const clear = () => {
    if (hasActiveBinding) return;
    setInput('');
    setParsed(null);
    setParseError(null);
    setBinding(null);
    setStatus(null);
    setApiError(null);
    setBusy(null);
    setSelectedMethod(null);
    notifiedBoundBinding.current = null;
  };

  const create = async () => {
    if (busy !== null || binding) return;
    setBusy('create');
    setApiError(null);
    setStatus(null);
    notifiedBoundBinding.current = null;
    try {
      setBinding(await createDeviceBinding());
    } catch (e) {
      setApiError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const refreshStatus = async () => {
    if (!binding?.binding_id) return;
    setBusy('status');
    setApiError(null);
    try {
      const next = await getDeviceBindingStatus(binding.binding_id);
      applyStatus(next, binding.binding_id);
    } catch (e) {
      setApiError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!binding?.binding_id) return;
    setBusy('cancel');
    setApiError(null);
    try {
      await cancelDeviceBinding(binding.binding_id);
      setStatus({ binding_id: binding.binding_id, status: 'cancelled' });
    } catch (e) {
      setApiError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card dv-add-panel" data-testid="pairing-panel">
      <div className="dv-pairing-head">
        <div>
          <h2>{t('devices.addCamera')}</h2>
          <p className="text-secondary">{t('devices.addCameraInstructions')}</p>
        </div>
        <div className="dv-pairing-actions">
          <button className="btn-ghost" onClick={clear} disabled={busy !== null || hasActiveBinding}>
            {t('devices.pairingClear')}
          </button>
          {onClose && (
            <button className="btn-ghost" onClick={onClose}>
              {t('common.close')}
            </button>
          )}
        </div>
      </div>

      <label className="dv-pairing-input">
        <span>{t('devices.pairingInputLabel')}</span>
        <textarea
          value={input}
          rows={4}
          spellCheck={false}
          placeholder={t('devices.pairingInputPlaceholder')}
          onChange={(e) => setInput(e.target.value)}
        />
      </label>

      <div className="dv-pairing-actions">
        <button className="btn-primary" onClick={parse} disabled={!input.trim() || busy !== null || hasActiveBinding}>
          {t('devices.pairingParse')}
        </button>
      </div>

      {parseError && <p className="error-text dv-pairing-message">{parseError}</p>}

      {parsed && (
        <section className="dv-pairing-summary" aria-live="polite">
          <h3>{t('devices.pairingParsedTitle')}</h3>
          <dl className="dv-kv">
            {qr && (
              <>
                <dt>{t('devices.pairingDeviceId')}</dt>
                <dd>{qr.deviceId}</dd>
                {qr.model && (
                  <>
                    <dt>{t('devices.pairingModel')}</dt>
                    <dd>{qr.model}</dd>
                  </>
                )}
              </>
            )}
            <dt>{t('devices.pairingMethods')}</dt>
            <dd>{methods}</dd>
            {qr?.softApSsid && (
              <>
                <dt>{t('devices.pairingSoftAp')}</dt>
                <dd>{qr.softApSsid}</dd>
                <dt>{t('devices.pairingSoftApPassword')}</dt>
                <dd>{qr.softApPassphrase ? t('devices.pairingSoftApPasswordIncluded') : t('devices.pairingSoftApPasswordMissing')}</dd>
              </>
            )}
            {qr?.bleSecret && (
              <>
                <dt>{t('devices.pairingBleSecret')}</dt>
                <dd>{t('devices.pairingBleSecretNative')}</dd>
              </>
            )}
            {qr?.region && (
              <>
                <dt>{t('devices.pairingRegion')}</dt>
                <dd>{qr.region}</dd>
              </>
            )}
          </dl>
          {availableMethods.length > 1 && !binding && (
            <fieldset className="dv-method-select">
              <legend>{t('devices.pairingProvisioningPreferred')}</legend>
              {availableMethods.map((method) => (
                <label key={method}>
                  <input
                    type="radio"
                    name="pairing-provisioning-method"
                    value={method}
                    checked={activeSelectedMethod === method}
                    disabled={busy !== null || hasActiveBinding}
                    onChange={() => setSelectedMethod(method)}
                  />
                  <span>{handoffMethodLabel(method)}</span>
                </label>
              ))}
            </fieldset>
          )}
          {!binding && (
            <div className="dv-pairing-actions">
              <button className="btn-primary" onClick={() => void create()} disabled={busy !== null}>
                {busy === 'create' ? t('devices.pairingCreatingBinding') : t('devices.pairingCreateBinding')}
              </button>
            </div>
          )}
        </section>
      )}

      {binding && (
        <section className="dv-pairing-summary dv-pairing-binding" aria-live="polite">
          <h3>{t('devices.pairingBindingTitle')}</h3>
          <p className="text-secondary dv-pairing-message">{t('devices.pairingTokenOnce')}</p>
          <dl className="dv-kv">
            <dt>{t('devices.pairingBindingId')}</dt>
            <dd>{binding.binding_id}</dd>
            <dt>{t('devices.pairingOwnerToken')}</dt>
            <dd><code>{binding.owner_token}</code></dd>
            <dt>{t('devices.pairingServerUrl')}</dt>
            <dd><code>{binding.server_url}</code></dd>
            {binding.region && (
              <>
                <dt>{t('devices.pairingRegion')}</dt>
                <dd>{binding.region}</dd>
              </>
            )}
            {typeof binding.expires_in === 'number' && (
              <>
                <dt>{t('devices.pairingExpiresIn')}</dt>
                <dd>{t('devices.pairingExpiresInSeconds', { count: binding.expires_in })}</dd>
              </>
            )}
            {status && (
              <>
                <dt>{t('devices.pairingStatus')}</dt>
                <dd>
                  {status.status}
                  {status.reason ? ` (${status.reason})` : ''}
                </dd>
                {status.device_id && (
                  <>
                    <dt>{t('devices.pairingBoundDevice')}</dt>
                    <dd>{status.device_id}</dd>
                  </>
                )}
              </>
            )}
          </dl>
          {hasActiveBinding && <p className="text-secondary dv-pairing-message">{t('devices.pairingStatusAutoPolling')}</p>}
          <div className="dv-pairing-actions">
            <button className="btn-ghost" onClick={() => void refreshStatus()} disabled={busy !== null || isTerminalStatus(status)}>
              {busy === 'status' ? t('devices.pairingCheckingStatus') : t('devices.pairingCheckStatus')}
            </button>
            <button className="btn-ghost" onClick={() => void cancel()} disabled={busy !== null || isTerminalStatus(status)}>
              {busy === 'cancel' ? t('devices.pairingCancelling') : t('common.cancel')}
            </button>
          </div>
        </section>
      )}

      {handoff && <PairingHandoff handoff={handoff} />}

      {apiError && <p className="error-text dv-pairing-message">{t('devices.pairingBindingFailed', { error: apiError })}</p>}
    </div>
  );
}
